// Export / import an automation as a downloadable file.
//
// This never talks to the network: it only names a file, turns the export envelope the
// server sent into a download, and turns a file the operator picked back into JSON. The
// server holds the one definition of what a valid envelope looks like -- the import mutation
// (useWorkflowData.ts) sends whatever this parses and reports back whatever the server refuses.

/** Mirrors the backend's import size cap (POST /api/agent/workflows/import answers 413 above it). */
export const MAX_IMPORT_BYTES = 1024 * 1024;

/**
 * "WhatsApp Lead Welcome & Qualifier" -> "whatsapp-lead-welcome-qualifier.automation.json".
 * A name that is blank, or entirely punctuation, still produces a usable file name.
 */
export function automationFileName(name: string): string {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug || "automation"}.automation.json`;
}

/** Blob -> object URL -> a temporary anchor click -> revoke. Nothing here is sent anywhere. */
export function downloadAutomationFile(doc: { name: string }): void {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(doc, null, 2)], { type: "application/json" }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = automationFileName(doc.name);
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("That file could not be read."));
    reader.onload = () => resolve(String(reader.result ?? ""));
    reader.readAsText(file);
  });
}

/**
 * Parse a picked file into the raw object an export envelope should be. This does not check
 * it actually IS one -- `format`, `version` and `graph` are the import endpoint's own checks,
 * and duplicating them here would be a second definition of the shape to keep in step with it.
 */
export async function readAutomationFile(file: File): Promise<unknown> {
  if (file.size > MAX_IMPORT_BYTES) {
    throw new Error("That file is larger than 1 MB and cannot be imported.");
  }
  const text = await readText(file);
  try {
    return JSON.parse(text);
  } catch {
    throw new Error("That file is not a valid automation export (not JSON).");
  }
}
